import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../config/api';
import LoadingButton from '../components/LoadingButton';
import {
  FiSave,
  FiZap,
  FiEdit3,
  FiArrowLeft,
  FiCheckCircle,
  FiAlertCircle,
  FiPlus,
  FiTrash2,
  FiUser,
  FiLink,
} from 'react-icons/fi';
import './ResumeStudio.css';
import './LinkedInStudio.css';

function ScoreRing({ score, label }) {
  if (score == null || score === '') {
    return (
      <div className="score-ring score-ring-low">
        <span className="score-value">—</span>
        <span className="score-label">{label}</span>
      </div>
    );
  }
  const color = score >= 75 ? 'good' : score >= 50 ? 'mid' : 'low';
  return (
    <div className={`score-ring score-ring-${color}`}>
      <span className="score-value">{score}</span>
      <span className="score-label">{label}</span>
    </div>
  );
}

const SECTION_LABELS = {
  headline: 'Headline',
  about: 'About',
  experience: 'Experience',
  education: 'Education',
  skills: 'Skills',
};

function emptyStructured() {
  return {
    profile_url: '',
    headline: '',
    about: '',
    experience: [],
    education: [],
    skills: [],
  };
}

function applySuggestion(structured, suggestion) {
  const next = { ...structured };
  const field = suggestion.field || suggestion.section;
  const text = suggestion.proposed_text || '';
  if (!text) return next;

  if (field === 'headline' || suggestion.section === 'headline') {
    next.headline = text;
    return next;
  }
  if (field === 'about' || suggestion.section === 'about') {
    next.about = text;
    return next;
  }
  if (field === 'skills' || suggestion.section === 'skills') {
    next.skills = text.split(/[,;\n]/).map((s) => s.trim()).filter(Boolean);
    return next;
  }
  if (suggestion.section === 'experience') {
    const idx =
      suggestion.exp_index != null
        ? Number(suggestion.exp_index)
        : next.experience.findIndex((e) => e.id === suggestion.item_id);
    if (idx >= 0 && next.experience[idx]) {
      const exp = [...next.experience];
      exp[idx] = { ...exp[idx], description: text };
      next.experience = exp;
    }
    return next;
  }
  if (suggestion.section === 'education') {
    const idx =
      suggestion.exp_index != null
        ? Number(suggestion.exp_index)
        : next.education.findIndex((e) => e.id === suggestion.item_id);
    if (idx >= 0 && next.education[idx]) {
      const edu = [...next.education];
      edu[idx] = { ...edu[idx], degree: text };
      next.education = edu;
    }
  }
  return next;
}

const LinkedInStudio = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('analysis');
  const [structured, setStructured] = useState(emptyStructured());
  const [profileText, setProfileText] = useState('');
  const [dirty, setDirty] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [scoreHistory, setScoreHistory] = useState([]);
  const [activeSuggestionIdx, setActiveSuggestionIdx] = useState(null);
  const [message, setMessage] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [needsPaste, setNeedsPaste] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [skillsInput, setSkillsInput] = useState('');
  const oauthReturnHandled = useRef(false);

  const { data: profile, isLoading, refetch: refetchProfile } = useQuery(
    'linkedin-profile-me',
    () => api.get('/linkedin-profiles/me').then((res) => res.data),
    {
      onSuccess: (data) => {
        if (!data) return;
        const s = data.structured_content || emptyStructured();
        setStructured({
          profile_url: data.profile_url || s.profile_url || '',
          headline: data.headline || s.headline || '',
          about: data.about || s.about || '',
          experience: s.experience || [],
          education: s.education || [],
          skills: s.skills || [],
        });
        setProfileText(data.profile_text || '');
        setSkillsInput((s.skills || []).join(', '));
        if (data.ai_insights?.last_analysis) setAnalysis(data.ai_insights.last_analysis);
        if (data.ai_insights?.last_suggestions) setSuggestions(data.ai_insights.last_suggestions);
        if (Array.isArray(data.ai_insights?.score_history)) {
          setScoreHistory(data.ai_insights.score_history);
        }
      },
    }
  );

  const { data: oauthStatus, refetch: refetchOauthStatus } = useQuery(
    'linkedin-oauth-status',
    () => api.get('/linkedin-oauth/status').then((res) => res.data),
    { retry: false }
  );

  const draftPayload = useCallback(
    () => ({
      structured_content: structured,
      profile_text: profileText,
      profile_url: structured.profile_url,
      headline: structured.headline,
      about: structured.about,
      experience: structured.experience,
      education: structured.education,
      skills: structured.skills,
    }),
    [structured, profileText]
  );

  const saveMutation = useMutation(
    () => api.put('/linkedin-profiles/me', draftPayload()),
    {
      onSuccess: () => {
        setDirty(false);
        setMessage('LinkedIn profile saved.');
        queryClient.invalidateQueries('linkedin-profile-me');
      },
      onError: (err) => setMessage(err.response?.data?.error || 'Save failed'),
    }
  );

  const parseMutation = useMutation(
    () => api.post(`/linkedin-profiles/${profile.id}/parse`, { profile_text: profileText }),
    {
      onSuccess: (res) => {
        const s = res.data.structured_content || emptyStructured();
        setStructured(s);
        setSkillsInput((s.skills || []).join(', '));
        setDirty(false);
        setMessage('Profile parsed into editable sections.');
        queryClient.invalidateQueries('linkedin-profile-me');
      },
      onError: (err) => setMessage(err.response?.data?.error || 'Parse failed'),
    }
  );

  const fetchSuggestions = useCallback(async () => {
    const res = await api.post(`/linkedin-profiles/${profile.id}/suggestions`, {
      ...draftPayload(),
      section: 'all',
    });
    const list = res.data.suggestions || [];
    setSuggestions(list);
    if (list.length > 0) {
      setActiveSuggestionIdx(0);
      setActiveTab('fixes');
    }
    return list;
  }, [draftPayload, profile?.id]);

  const runAnalyze = async ({ rescore = false } = {}) => {
    setAiBusy(true);
    try {
      if (dirty) {
        await api.put('/linkedin-profiles/me', draftPayload());
        setDirty(false);
      }
      const res = await api.post(`/linkedin-profiles/${profile.id}/analyze`, {
        ...draftPayload(),
        draft_only: false,
        rescore,
      });
      const a = res.data.analysis;
      setAnalysis(a);
      if (Array.isArray(res.data.score_history)) setScoreHistory(res.data.score_history);
      const sugList = res.data.suggestions || [];
      if (sugList.length) {
        setSuggestions(sugList);
        setActiveSuggestionIdx(0);
        setActiveTab('fixes');
      } else {
        setActiveTab('analysis');
      }
      setMessage(
        rescore
          ? `Rescored: ${a.overall_score}/100 (grammar ${a.grammar_score ?? '—'}, length ${a.content_length_score ?? '—'}). Apply fixes and iterate.`
          : `Foundation score ${a.overall_score}/100 — grammar, language & content length. Apply fixes, edit, then Rescore.`
      );
    } catch (err) {
      setMessage(err.response?.data?.error || err.message || 'Analysis failed');
    } finally {
      setAiBusy(false);
    }
  };

  const applyImportedProfile = (data, fallbackUrl = '') => {
    const s = data.structured_content || emptyStructured();
    setStructured({
      profile_url: data.profile_url || s.profile_url || fallbackUrl,
      headline: data.headline || s.headline || '',
      about: data.about || s.about || '',
      experience: s.experience || [],
      education: s.education || [],
      skills: s.skills || [],
    });
    setProfileText(data.profile_text || '');
    setSkillsInput((s.skills || []).join(', '));
  };

  const scoreAfterImport = async (data, url) => {
    const s = data.structured_content || emptyStructured();
    setMessage(data.import?.message || 'Profile imported from LinkedIn Official API. Scoring…');
    const analyzeRes = await api.post(`/linkedin-profiles/${profile.id}/analyze`, {
      structured_content: s,
      profile_text: data.profile_text || '',
      profile_url: data.profile_url || url,
      draft_only: false,
    });
    const a = analyzeRes.data.analysis;
    setAnalysis(a);
    if (Array.isArray(analyzeRes.data.score_history)) setScoreHistory(analyzeRes.data.score_history);
    const sugList = analyzeRes.data.suggestions || [];
    if (sugList.length) {
      setSuggestions(sugList);
      setActiveSuggestionIdx(0);
      setActiveTab('fixes');
    } else {
      setActiveTab('analysis');
    }
    const thin =
      !(s.about || '').trim() ||
      !(s.experience || []).length ||
      !(s.experience || []).some((e) => (e.description || '').trim());
    if (thin) setNeedsPaste(true);
    setMessage(
      `Imported via Official API & scored ${a.overall_score}/100.${
        thin ? ' Paste About / full Experience if you want richer scoring.' : ''
      }`
    );
  };

  const startLinkedInOauth = async (url) => {
    const startRes = await api.post('/linkedin-oauth/start', { profile_url: url });
    const authorizeUrl = startRes.data?.authorize_url;
    if (!authorizeUrl) throw new Error('No authorize URL returned');
    setMessage('Redirecting to LinkedIn to authorize Official API access…');
    window.location.href = authorizeUrl;
  };

  const runImportAndAnalyze = async () => {
    const url = (structured.profile_url || '').trim();
    if (!url) {
      setMessage('Enter your LinkedIn profile URL first.');
      return;
    }
    setAiBusy(true);
    setNeedsPaste(false);
    try {
      await api.put('/linkedin-profiles/me', draftPayload());
      setDirty(false);

      const status =
        oauthStatus ||
        (await api.get('/linkedin-oauth/status').then((r) => r.data).catch(() => null));

      if (!status?.configured) {
        setMessage(
          'LinkedIn Official API is not configured. An admin must set Client ID and Client Secret under Settings → LinkedIn API.'
        );
        return;
      }

      if (!status.connected) {
        await startLinkedInOauth(url);
        return;
      }

      let importRes;
      try {
        importRes = await api.post(`/linkedin-profiles/${profile.id}/import-from-api`, {
          profile_url: url,
        });
      } catch (err) {
        if (err.response?.data?.needs_oauth || err.response?.status === 401) {
          await startLinkedInOauth(url);
          return;
        }
        throw err;
      }

      const data = importRes.data;
      applyImportedProfile(data, url);
      queryClient.invalidateQueries('linkedin-profile-me');
      queryClient.invalidateQueries('linkedin-oauth-status');
      await scoreAfterImport(data, url);
    } catch (err) {
      const data = err.response?.data;
      if (data?.needs_config) {
        setMessage(data.error);
      } else if (data?.needs_oauth) {
        try {
          await startLinkedInOauth(url);
          return;
        } catch (e2) {
          setMessage(e2.response?.data?.error || e2.message || 'Could not start LinkedIn OAuth');
        }
      } else {
        setMessage(data?.error || data?.import?.message || err.message || 'Import failed');
      }
    } finally {
      setAiBusy(false);
    }
  };

  // After LinkedIn OAuth callback redirect
  useEffect(() => {
    const li = searchParams.get('linkedin');
    if (!li || oauthReturnHandled.current || !profile?.id) return;
    oauthReturnHandled.current = true;

    const clearParams = () => {
      const next = new URLSearchParams(searchParams);
      next.delete('linkedin');
      next.delete('analyze');
      next.delete('message');
      next.delete('sources');
      setSearchParams(next, { replace: true });
    };

    if (li === 'error') {
      setMessage(searchParams.get('message') || 'LinkedIn authorization failed');
      clearParams();
      return;
    }

    if (li === 'connected') {
      (async () => {
        setAiBusy(true);
        try {
          await refetchOauthStatus();
          const { data } = await refetchProfile();
          if (data) applyImportedProfile(data, structured.profile_url);
          if (searchParams.get('analyze') === '1' && data) {
            await scoreAfterImport(
              {
                ...data,
                import: { message: 'Connected to LinkedIn Official API.' },
              },
              data.profile_url || structured.profile_url
            );
          } else {
            setMessage('LinkedIn connected. Profile loaded from Official API.');
          }
        } catch (err) {
          setMessage(err.response?.data?.error || err.message || 'Post-connect analyze failed');
          setNeedsPaste(true);
          setPasteOpen(true);
        } finally {
          setAiBusy(false);
          clearParams();
        }
      })();
    }
    // One-shot OAuth return handler (guarded by oauthReturnHandled). Intentional deps only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, profile?.id]);

  const runSuggestionsOnly = async () => {
    setAiBusy(true);
    try {
      const list = await fetchSuggestions();
      setMessage(
        list.length
          ? `${list.length} suggestions ready — Apply, edit, then Rescore.`
          : 'No suggestions returned. Add more profile content or check AI settings.'
      );
    } catch (err) {
      setMessage(err.response?.data?.error || err.message || 'Suggestions failed');
    } finally {
      setAiBusy(false);
    }
  };

  const handleApplySuggestion = (idx) => {
    const s = suggestions[idx];
    if (!s?.proposed_text) {
      setActiveSuggestionIdx(idx);
      setMessage('No auto-apply text — use the suggestion as guidance and edit the field.');
      return;
    }
    setStructured(applySuggestion(structured, s));
    if (s.section === 'skills' || s.field === 'skills') {
      const next = applySuggestion(structured, s);
      setSkillsInput((next.skills || []).join(', '));
    }
    setDirty(true);
    setActiveSuggestionIdx(idx);
    setMessage('Fix applied — review and save.');
  };

  const updateField = (key, value) => {
    setStructured((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  const hasEditorContent = useMemo(() => {
    return Boolean(
      profileText?.trim() ||
        structured.headline?.trim() ||
        structured.about?.trim() ||
        structured.experience?.length ||
        structured.skills?.length
    );
  }, [profileText, structured]);

  const hasUrl = Boolean((structured.profile_url || '').trim());
  const canIterate = Boolean(analysis);

  const sectionScoreEntries = useMemo(() => {
    const scores = analysis?.section_scores || {};
    return Object.entries(scores).filter(([, v]) => v != null && v !== '');
  }, [analysis]);

  useEffect(() => {
    if (activeSuggestionIdx == null) return;
    const s = suggestions[activeSuggestionIdx];
    if (!s) return;
    const el = document.querySelector(`[data-li-section="${s.section}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [activeSuggestionIdx, suggestions]);

  if (isLoading) {
    return <div className="loading">Loading LinkedIn Studio…</div>;
  }

  if (!profile) {
    return <div className="error">Could not load LinkedIn profile</div>;
  }

  const activeSection = suggestions[activeSuggestionIdx]?.section;

  return (
    <div className="resume-studio linkedin-studio">
      <header className="studio-header">
        <div className="studio-header-left">
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => navigate('/ai-studio')}>
            <FiArrowLeft /> AI Studio
          </button>
          <div>
            <h1>
              <FiUser aria-hidden /> LinkedIn Profile Enhancement
            </h1>
            <p className="studio-subtitle">
              Enter your LinkedIn URL → connect via Official API → score grammar, language &amp; content
              length → apply fixes → rescore.
            </p>
          </div>
        </div>
        <div className="studio-header-actions">
          {profileText?.trim() && (
            <LoadingButton
              className="btn btn-secondary"
              loading={parseMutation.isLoading}
              loadingLabel="Parsing…"
              onClick={() => parseMutation.mutate()}
            >
              Parse into sections
            </LoadingButton>
          )}
          <LoadingButton
            className="btn btn-secondary"
            icon={FiLink}
            loading={aiBusy}
            loadingLabel="Working…"
            onClick={runImportAndAnalyze}
            disabled={!hasUrl}
            title="Connect LinkedIn Official API, fetch profile, then score"
          >
            {oauthStatus?.connected ? 'Refresh & analyze' : 'Analyze'}
          </LoadingButton>
          <LoadingButton
            className="btn btn-primary"
            icon={FiSave}
            loading={saveMutation.isLoading}
            loadingLabel="Saving…"
            onClick={() => saveMutation.mutate()}
            disabled={!dirty && !saveMutation.isLoading}
          >
            {dirty ? 'Save changes' : 'Saved'}
          </LoadingButton>
        </div>
      </header>

      {!hasUrl && !hasEditorContent && (
        <div className="studio-message studio-message-warn">
          Step 1: enter your LinkedIn profile URL and click <strong>Analyze</strong>. You will authorize
          LinkedIn once so we can load your profile via the Official API.
        </div>
      )}

      {oauthStatus && !oauthStatus.configured && (
        <div className="studio-message studio-message-warn">
          LinkedIn Official API is not configured yet. An admin must add Client ID and Client Secret under{' '}
          <strong>Settings → LinkedIn API</strong>.
        </div>
      )}

      {oauthStatus?.connected && (
        <div className="studio-message" role="status">
          LinkedIn Official API connected
          {oauthStatus.connected_at ? ` · ${new Date(oauthStatus.connected_at).toLocaleString()}` : ''}.
        </div>
      )}

      {needsPaste && (
        <div className="studio-message studio-message-warn">
          Official API returned identity fields. For a fuller About / Experience history, paste those
          sections below, then Analyze or Rescore.
        </div>
      )}

      {message && (
        <div className="studio-message" role="status">
          {message}
          <button type="button" className="studio-message-dismiss" onClick={() => setMessage('')}>
            ×
          </button>
        </div>
      )}

      <div className="studio-layout">
        <div className="studio-editor-pane">
          <div className="studio-pane-title">
            <FiEdit3 /> Profile editor
            {suggestions.length > 0 && (
              <span className="badge badge-warning">{suggestions.length} fixes to review</span>
            )}
          </div>

          <div className="li-editor">
            <label className="li-field" data-li-section="url">
              <span>LinkedIn profile URL</span>
              <div className="li-url-row">
                <input
                  type="url"
                  placeholder="https://www.linkedin.com/in/…"
                  value={structured.profile_url}
                  onChange={(e) => updateField('profile_url', e.target.value)}
                />
                <LoadingButton
                  type="button"
                  className="btn btn-primary"
                  loading={aiBusy}
                  loadingLabel="…"
                  onClick={runImportAndAnalyze}
                  disabled={!hasUrl}
                >
                  {oauthStatus?.connected ? 'Refresh & analyze' : 'Analyze'}
                </LoadingButton>
              </div>
            </label>

            <label className={`li-field ${activeSection === 'headline' ? 'li-field-active' : ''}`} data-li-section="headline">
              <span>Headline</span>
              <input
                type="text"
                placeholder="Role · Specialty · Value (e.g. Senior PM | B2B SaaS | 0→1 products)"
                value={structured.headline}
                onChange={(e) => updateField('headline', e.target.value)}
              />
            </label>

            <label className={`li-field ${activeSection === 'about' ? 'li-field-active' : ''}`} data-li-section="about">
              <span>About</span>
              <textarea
                rows={6}
                placeholder="Who you help, what you do, proof points…"
                value={structured.about}
                onChange={(e) => updateField('about', e.target.value)}
              />
            </label>

            <div className={`li-section ${activeSection === 'experience' ? 'li-field-active' : ''}`} data-li-section="experience">
              <div className="li-section-head">
                <h3>Experience</h3>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    updateField('experience', [
                      ...structured.experience,
                      {
                        id: `exp${Date.now()}`,
                        title: '',
                        company: '',
                        dates: '',
                        description: '',
                      },
                    ]);
                  }}
                >
                  <FiPlus /> Add
                </button>
              </div>
              {structured.experience.map((exp, idx) => (
                <div key={exp.id || idx} className="li-exp-card">
                  <div className="li-exp-row">
                    <input
                      placeholder="Title"
                      value={exp.title || ''}
                      onChange={(e) => {
                        const experience = [...structured.experience];
                        experience[idx] = { ...exp, title: e.target.value };
                        updateField('experience', experience);
                      }}
                    />
                    <input
                      placeholder="Company"
                      value={exp.company || ''}
                      onChange={(e) => {
                        const experience = [...structured.experience];
                        experience[idx] = { ...exp, company: e.target.value };
                        updateField('experience', experience);
                      }}
                    />
                    <input
                      placeholder="Dates"
                      value={exp.dates || ''}
                      onChange={(e) => {
                        const experience = [...structured.experience];
                        experience[idx] = { ...exp, dates: e.target.value };
                        updateField('experience', experience);
                      }}
                    />
                    <IconTrash
                      onClick={() => {
                        updateField(
                          'experience',
                          structured.experience.filter((_, i) => i !== idx)
                        );
                      }}
                    />
                  </div>
                  <textarea
                    rows={3}
                    placeholder="Description / achievements"
                    value={exp.description || ''}
                    onChange={(e) => {
                      const experience = [...structured.experience];
                      experience[idx] = { ...exp, description: e.target.value };
                      updateField('experience', experience);
                    }}
                  />
                </div>
              ))}
            </div>

            <div className={`li-section ${activeSection === 'education' ? 'li-field-active' : ''}`} data-li-section="education">
              <div className="li-section-head">
                <h3>Education</h3>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    updateField('education', [
                      ...structured.education,
                      { id: `edu${Date.now()}`, degree: '', school: '', dates: '' },
                    ]);
                  }}
                >
                  <FiPlus /> Add
                </button>
              </div>
              {structured.education.map((edu, idx) => (
                <div key={edu.id || idx} className="li-exp-row">
                  <input
                    placeholder="Degree"
                    value={edu.degree || ''}
                    onChange={(e) => {
                      const education = [...structured.education];
                      education[idx] = { ...edu, degree: e.target.value };
                      updateField('education', education);
                    }}
                  />
                  <input
                    placeholder="School"
                    value={edu.school || ''}
                    onChange={(e) => {
                      const education = [...structured.education];
                      education[idx] = { ...edu, school: e.target.value };
                      updateField('education', education);
                    }}
                  />
                  <input
                    placeholder="Dates"
                    value={edu.dates || ''}
                    onChange={(e) => {
                      const education = [...structured.education];
                      education[idx] = { ...edu, dates: e.target.value };
                      updateField('education', education);
                    }}
                  />
                  <IconTrash
                    onClick={() => {
                      updateField(
                        'education',
                        structured.education.filter((_, i) => i !== idx)
                      );
                    }}
                  />
                </div>
              ))}
            </div>

            <label className={`li-field ${activeSection === 'skills' ? 'li-field-active' : ''}`} data-li-section="skills">
              <span>Skills (comma-separated)</span>
              <input
                type="text"
                placeholder="Product strategy, SQL, Stakeholder management"
                value={skillsInput}
                onChange={(e) => {
                  setSkillsInput(e.target.value);
                  updateField(
                    'skills',
                    e.target.value.split(',').map((s) => s.trim()).filter(Boolean)
                  );
                }}
              />
            </label>

            <details className="raw-text-details" open={pasteOpen || needsPaste || (!structured.headline && !structured.about)}>
              <summary>Paste About / Experience (optional — Official API does not return full history)</summary>
              <textarea
                rows={8}
                placeholder="Paste your LinkedIn About + Experience text here, then Analyze…"
                value={profileText}
                onChange={(e) => {
                  setProfileText(e.target.value);
                  setDirty(true);
                  setNeedsPaste(false);
                }}
              />
            </details>
          </div>
        </div>

        <aside className="studio-insights-pane">
          <div className="studio-tabs">
            {[
              { id: 'analysis', label: 'AI review', icon: FiZap },
              { id: 'fixes', label: `Fixes${suggestions.length ? ` (${suggestions.length})` : ''}`, icon: FiEdit3 },
            ].map(({ id: tabId, label, icon: Icon }) => (
              <button
                key={tabId}
                type="button"
                className={`studio-tab ${activeTab === tabId ? 'active' : ''}`}
                onClick={() => setActiveTab(tabId)}
              >
                <Icon /> {label}
              </button>
            ))}
          </div>

          {activeTab === 'analysis' && (
            <div className="studio-panel">
              <p className="panel-intro">
                Foundation pass scores <strong>grammar</strong>, <strong>language</strong>, and{' '}
                <strong>content length</strong>. Apply Fixes, edit the profile, then Rescore — repeat until you like the score.
              </p>
              <LoadingButton
                className="btn btn-primary btn-block"
                icon={FiZap}
                loading={aiBusy}
                loadingLabel={canIterate ? 'Rescoring…' : 'Analyzing…'}
                onClick={() => runAnalyze({ rescore: canIterate })}
                disabled={!hasEditorContent && !hasUrl}
              >
                {canIterate ? 'Rescore after edits' : 'Analyze & get fixes'}
              </LoadingButton>
              {hasUrl && !hasEditorContent && (
                <LoadingButton
                  className="btn btn-secondary btn-block"
                  style={{ marginTop: 8 }}
                  loading={aiBusy}
                  loadingLabel="Importing…"
                  onClick={runImportAndAnalyze}
                >
                  Import from URL first
                </LoadingButton>
              )}
              {analysis && (
                <div className="analysis-results">
                  <div className="score-row">
                    <ScoreRing score={analysis.overall_score} label="Overall" />
                    <ScoreRing score={analysis.grammar_score} label="Grammar" />
                    <ScoreRing score={analysis.language_score} label="Language" />
                    <ScoreRing score={analysis.content_length_score} label="Length" />
                  </div>
                  {sectionScoreEntries.length > 0 && (
                    <div className="li-section-scores">
                      <h4>Section scores</h4>
                      <div className="li-section-score-grid">
                        {sectionScoreEntries.map(([key, value]) => (
                          <ScoreRing key={key} score={value} label={SECTION_LABELS[key] || key} />
                        ))}
                      </div>
                    </div>
                  )}
                  {analysis.summary && <p className="fit-summary">{analysis.summary}</p>}
                  {analysis.strengths?.length > 0 && (
                    <div className="insight-block insight-good">
                      <h4>
                        <FiCheckCircle /> Strengths
                      </h4>
                      <ul>
                        {analysis.strengths.map((s, i) => (
                          <li key={i}>{s}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {analysis.gaps?.length > 0 && (
                    <div className="insight-block insight-warn">
                      <h4>
                        <FiAlertCircle /> Gaps
                      </h4>
                      <ul>
                        {analysis.gaps.map((s, i) => (
                          <li key={i}>{s}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {analysis.tips?.length > 0 && (
                    <div className="insight-block">
                      <h4>Recommendations</h4>
                      <ul>
                        {analysis.tips.map((s, i) => (
                          <li key={i}>{s}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {scoreHistory.length > 1 && (
                    <div className="li-score-history">
                      <h4>Score history (iteration)</h4>
                      <ol>
                        {scoreHistory.slice(0, 6).map((h, i) => (
                          <li key={h.analyzed_at || i}>
                            {h.overall_score ?? '—'} overall
                            {h.grammar_score != null ? ` · grammar ${h.grammar_score}` : ''}
                            {h.content_length_score != null ? ` · length ${h.content_length_score}` : ''}
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'fixes' && (
            <div className="studio-panel">
              <p className="panel-intro">
                Click a fix to highlight the section, then Apply to insert the AI rewrite.
              </p>
              <LoadingButton
                className="btn btn-primary btn-block"
                icon={FiEdit3}
                loading={aiBusy}
                loadingLabel="Loading…"
                onClick={runSuggestionsOnly}
                disabled={!hasEditorContent}
              >
                Refresh fix suggestions
              </LoadingButton>
              <ul className="suggestions-list">
                {suggestions.map((s, idx) => (
                  <li
                    key={s.id || idx}
                    className={`suggestion-item severity-${s.severity} ${activeSuggestionIdx === idx ? 'active' : ''}`}
                  >
                    <button
                      type="button"
                      className="suggestion-focus-btn"
                      onClick={() => setActiveSuggestionIdx(idx)}
                    >
                      <span className="suggestion-section">{SECTION_LABELS[s.section] || s.section}</span>
                      <strong>{s.issue}</strong>
                      <p>{s.suggestion}</p>
                    </button>
                    {s.proposed_text && (
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm suggestion-apply-btn"
                        onClick={() => handleApplySuggestion(idx)}
                      >
                        Apply fix
                      </button>
                    )}
                  </li>
                ))}
              </ul>
              {suggestions.length === 0 && (
                <p className="panel-intro">No fixes yet — run Analyze or Refresh suggestions.</p>
              )}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
};

function IconTrash({ onClick }) {
  return (
    <button type="button" className="btn btn-secondary btn-sm li-trash" onClick={onClick} aria-label="Remove">
      <FiTrash2 />
    </button>
  );
}

export default LinkedInStudio;
